import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Search } from 'lucide-react';
import {
  BackLink, Button, Card, CardTitle, Container, Empty, Input, Message, Subtitle, Title,
} from '../admin/ui';
import { errorText } from '../personAdmin/personAdminModel';
import { formatMoment } from '../personAdmin/personAdminParts';
import { PERMISSIONS, useCan } from '../authority/permissions';
import * as Api from './StaffAdminApi';

// The Team screen (owner 2026-10-25), admins only: who works in the admin area, giving an existing
// account the TEAM or ADMIN role or taking it back, what each role may do (the backend's table,
// authority.Permission), and the activity trail - every change an admin or team member made, newest
// first. There is no invitation of new staff: the person signs up first and is then found here.
// The last admin cannot lose the role (the backend refuses with a message shown here).

const Table = ({ children }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>{children}</table>
  </div>
);
const Th = ({ children }) => (
  <th style={{ padding: '.4rem .6rem', fontWeight: 700, borderBottom: '2px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children, style }) => <td style={{ padding: '.45rem .6rem', verticalAlign: 'top', ...style }}>{children}</td>;
const Row = ({ children }) => <tr style={{ borderTop: '1px solid #eef2f7' }}>{children}</tr>;
const Actions = ({ children }) => <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>{children}</div>;

const TeamManagement = () => {
  const intl = useIntl();
  const t = useCallback((id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values), [intl]);

  const [staff, setStaff] = useState(null);
  const [table, setTable] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityOf, setActivityOf] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // A change that takes access away is asked once: {accountId, role, name}.
  const [confirm, setConfirm] = useState(null);

  const roleLabel = (role) => t(`staffAdmin.role.${role}`, role);

  const loadStaff = useCallback(async () => {
    try {
      setStaff(await Api.fetchStaff());
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('staffAdmin.loadFailed', 'Could not load the team.')) });
    }
  }, [t]);

  const loadActivity = useCallback(async (accountId) => {
    try {
      setActivity(await Api.fetchActivity(accountId));
    } catch (err) {
      setActivity([]);
    }
  }, []);

  useEffect(() => {
    loadStaff();
    Api.fetchPermissionTable().then(setTable).catch(() => setTable([]));
  }, [loadStaff]);

  useEffect(() => { loadActivity(activityOf ? activityOf.accountId : null); }, [activityOf, loadActivity]);

  const search = async (event) => {
    event.preventDefault();
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      setResults(await Api.searchAccounts(query.trim()));
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('staffAdmin.searchFailed', 'The search failed.')) });
    }
  };

  const change = async (account, role) => {
    setConfirm(null);
    setBusy(true);
    setMessage(null);
    try {
      const updated = await Api.setRole(account.accountId, role);
      setMessage({ error: false, text: t('staffAdmin.changed', '{name} is now: {role}.', { name: updated.name || updated.email, role: roleLabel(updated.role) }) });
      if (results) setResults(results.map((r) => (r.accountId === updated.accountId ? updated : r)));
      await loadStaff();
      await loadActivity(activityOf ? activityOf.accountId : null);
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('staffAdmin.changeFailed', 'The role could not be changed.')) });
    } finally {
      setBusy(false);
    }
  };

  // Making someone staff happens at once; taking access away (or making an admin, who can then do
  // everything) is asked first.
  const ask = (account, role) => {
    if (role === 'TEAM' && account.role === 'MEMBER') change(account, role);
    else setConfirm({ account, role });
  };

  const roleButtons = (account) => (
    <Actions>
      {account.role !== 'TEAM' && (
        <Button type="button" secondary disabled={busy} onClick={() => ask(account, 'TEAM')}>
          {account.role === 'ADMIN'
            ? <FormattedMessage id="staffAdmin.makeTeamFromAdmin" defaultMessage="Make team member (no admin)" />
            : <FormattedMessage id="staffAdmin.makeTeam" defaultMessage="Add to the team" />}
        </Button>
      )}
      {account.role !== 'ADMIN' && (
        <Button type="button" secondary disabled={busy} onClick={() => ask(account, 'ADMIN')}>
          <FormattedMessage id="staffAdmin.makeAdmin" defaultMessage="Make admin" />
        </Button>
      )}
      {account.role !== 'MEMBER' && (
        <Button type="button" danger disabled={busy} onClick={() => ask(account, 'MEMBER')}>
          <FormattedMessage id="staffAdmin.remove" defaultMessage="Remove from the team" />
        </Button>
      )}
    </Actions>
  );

  const confirmText = (c) => {
    const name = c.account.name || c.account.email;
    if (c.role === 'ADMIN') {
      return t('staffAdmin.confirm.admin', '{name} will be an admin: they can do everything, including giving and taking these roles, deleting and merging people, and deleting events.', { name });
    }
    if (c.role === 'TEAM') {
      return t('staffAdmin.confirm.team', '{name} will no longer be an admin. They keep working in the admin area as a team member.', { name });
    }
    return t('staffAdmin.confirm.remove', '{name} will no longer have access to the admin area. Their own profile and login stay.', { name });
  };

  const nameOf = (accountId) => {
    const row = (staff || []).find((s) => s.accountId === accountId);
    return row ? (row.name || row.email) : `#${accountId}`;
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard"><FormattedMessage id="staffAdmin.back" defaultMessage="Back to the dashboard" /></BackLink>
      <Title><FormattedMessage id="staffAdmin.title" defaultMessage="Team" /></Title>
      <Subtitle>
        <FormattedMessage id="staffAdmin.subtitle"
          defaultMessage="Who works in the admin area. A team member does the daily work; an admin can also manage the team and do the few things that cannot be undone." />
      </Subtitle>

      {message && <Message error={message.error} role={message.error ? 'alert' : 'status'}>{message.text}</Message>}
      {confirm && (
        <Message warn role="alertdialog">
          <div>{confirmText(confirm)}</div>
          <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
            <Button type="button" danger={confirm.role !== 'ADMIN'} disabled={busy} onClick={() => change(confirm.account, confirm.role)}>
              <FormattedMessage id="staffAdmin.confirm.yes" defaultMessage="Change the role" />
            </Button>
            <Button type="button" secondary disabled={busy} onClick={() => setConfirm(null)}>
              <FormattedMessage id="staffAdmin.confirm.no" defaultMessage="Cancel" />
            </Button>
          </div>
        </Message>
      )}

      <Card>
        <CardTitle><FormattedMessage id="staffAdmin.staff.title" defaultMessage="Admins and team members" /></CardTitle>
        {staff === null ? (
          <Empty><FormattedMessage id="staffAdmin.loading" defaultMessage="Loading…" /></Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th><FormattedMessage id="staffAdmin.col.name" defaultMessage="Name" /></Th>
                <Th><FormattedMessage id="staffAdmin.col.email" defaultMessage="Login" /></Th>
                <Th><FormattedMessage id="staffAdmin.col.role" defaultMessage="Role" /></Th>
                <Th><FormattedMessage id="staffAdmin.col.lastActivity" defaultMessage="Last change" /></Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {staff.map((account) => (
                <Row key={account.accountId}>
                  <Td>{account.name || '—'}</Td>
                  <Td style={{ wordBreak: 'break-all' }}>{account.email}</Td>
                  <Td><strong>{roleLabel(account.role)}</strong></Td>
                  <Td>
                    {account.lastActivityAt ? formatMoment(intl, account.lastActivityAt) : '—'}
                    {' · '}
                    <button type="button" onClick={() => setActivityOf(account)}
                      style={{ border: 0, background: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, font: 'inherit' }}>
                      <FormattedMessage id="staffAdmin.showActivity" defaultMessage="Show changes" />
                    </button>
                  </Td>
                  <Td>{roleButtons(account)}</Td>
                </Row>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="staffAdmin.add.title" defaultMessage="Add someone" /></CardTitle>
        <p style={{ margin: '0 0 .75rem', fontSize: '.82rem', color: '#64748b' }}>
          <FormattedMessage id="staffAdmin.add.hint"
            defaultMessage="Only an existing account can get a role. If the person has no account yet, they sign up on the website first." />
        </p>
        <form onSubmit={search} style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} style={{ flex: '1 1 16rem' }}
            aria-label={t('staffAdmin.add.search', 'Name or email')} placeholder={t('staffAdmin.add.search', 'Name or email')} />
          <Button type="submit"><Search size={16} /><FormattedMessage id="staffAdmin.add.searchButton" defaultMessage="Search" /></Button>
        </form>
        {results !== null && (results.length === 0 ? (
          <Empty style={{ padding: '.5rem 0', textAlign: 'left' }}>
            <FormattedMessage id="staffAdmin.add.none" defaultMessage="No account found (at least two characters)." />
          </Empty>
        ) : (
          <Table>
            <tbody>
              {results.map((account) => (
                <Row key={account.accountId}>
                  <Td>{account.name || '—'}</Td>
                  <Td style={{ wordBreak: 'break-all' }}>{account.email}</Td>
                  <Td>{roleLabel(account.role)}</Td>
                  <Td>{roleButtons(account)}</Td>
                </Row>
              ))}
            </tbody>
          </Table>
        ))}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="staffAdmin.rights.title" defaultMessage="What each role may do" /></CardTitle>
        <Table>
          <thead>
            <tr>
              <Th />
              <Th>{roleLabel('TEAM')}</Th>
              <Th>{roleLabel('ADMIN')}</Th>
            </tr>
          </thead>
          <tbody>
            {table.map((row) => (
              <Row key={row.key}>
                <Td>{t(`staffAdmin.permission.${row.key}`, row.key)}</Td>
                <Td>{row.roles.includes('TEAM') ? '✓' : '—'}</Td>
                <Td>{row.roles.includes('ADMIN') ? '✓' : '—'}</Td>
              </Row>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card>
        <CardTitle>
          {activityOf
            ? <FormattedMessage id="staffAdmin.activity.of" defaultMessage="Changes by {name}" values={{ name: activityOf.name || activityOf.email }} />
            : <FormattedMessage id="staffAdmin.activity.title" defaultMessage="Latest changes" />}
        </CardTitle>
        {activityOf && (
          <p style={{ margin: '0 0 .75rem' }}>
            <Button type="button" secondary onClick={() => setActivityOf(null)}>
              <FormattedMessage id="staffAdmin.activity.everyone" defaultMessage="Show everyone" />
            </Button>
          </p>
        )}
        <p style={{ margin: '0 0 .75rem', fontSize: '.82rem', color: '#64748b' }}>
          <FormattedMessage id="staffAdmin.activity.hint"
            defaultMessage="Every change made in the admin area, with the address it was made at. Reading is not recorded." />
        </p>
        {activity.length === 0 ? (
          <Empty style={{ padding: '.5rem 0', textAlign: 'left' }}>
            <FormattedMessage id="staffAdmin.activity.none" defaultMessage="Nothing recorded yet." />
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th><FormattedMessage id="staffAdmin.activity.when" defaultMessage="When" /></Th>
                <Th><FormattedMessage id="staffAdmin.activity.who" defaultMessage="Who" /></Th>
                <Th><FormattedMessage id="staffAdmin.activity.what" defaultMessage="What" /></Th>
              </tr>
            </thead>
            <tbody>
              {activity.map((row) => (
                <Row key={row.id}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatMoment(intl, row.occurredAt)}</Td>
                  <Td>{nameOf(row.accountId)}</Td>
                  <Td style={{ wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace', fontSize: '.78rem' }}>
                    {row.method} {row.path}
                  </Td>
                </Row>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
};

// A team member who opens the address sees why there is nothing, instead of a list of refusals.
const TeamScreen = () => {
  const can = useCan();
  if (!can(PERMISSIONS.staffManage)) {
    return (
      <Container>
        <BackLink to="/admin/dashboard"><FormattedMessage id="staffAdmin.back" defaultMessage="Back to the dashboard" /></BackLink>
        <Message warn role="status">
          <FormattedMessage id="staffAdmin.adminOnly" defaultMessage="Only an admin can manage the team." />
        </Message>
      </Container>
    );
  }
  return <TeamManagement />;
};

export default TeamScreen;
